import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  addDoc
} from 'firebase/firestore';
import { db } from './config';
import { ClimbingRoute } from '../../models/climbing/ClimbingRoute';
import { Gym } from '../../models/gym/Gym';

// Get all climbing routes for a gym
export const getClimbingRoutes = async (gymId: string): Promise<ClimbingRoute[]> => {
  try {
    console.log(`Fetching climbing routes for gym: ${gymId}`);
    
    // Get the gym document
    const gymRef = doc(db, 'gyms', gymId);
    const gymSnap = await getDoc(gymRef);
    
    if (!gymSnap.exists()) {
      console.error(`Error: Gym with ID ${gymId} does not exist!`);
      return [];
    }
    
    // Create the gym object from the data
    const gymDocData = gymSnap.data();
    const gym = Gym.fromFirestore({ ...gymDocData, id: gymId });
    
    // Get all routes from the gym
    const routes = gym.getClimbingRoutes();
    console.log(`Found ${routes.length} climbing routes for gym ${gymId}`);
    
    return routes;
  } catch (error) {
    console.error('Error getting climbing routes:', error);
    throw error;
  }
};

// Get a specific climbing route
export const getClimbingRoute = async (gymId: string, routeId: string): Promise<ClimbingRoute | null> => {
  try {
    console.log(`Fetching climbing route ${routeId} for gym ${gymId}`);
    
    // Get the gym document
    const gymRef = doc(db, 'gyms', gymId);
    const gymSnap = await getDoc(gymRef);
    
    if (!gymSnap.exists()) {
      console.error(`Gym with ID ${gymId} does not exist!`);
      return null;
    }
    
    // Create the gym object from the data
    const gymDocData = gymSnap.data();
    const gym = Gym.fromFirestore({ ...gymDocData, id: gymId });
    
    // Find the specific route
    const routes = gym.getClimbingRoutes();
    const route = routes.find(r => r.id === routeId);
    
    if (!route) {
      console.log(`No climbing route found with ID ${routeId}`);
      return null;
    }
    
    return route;
  } catch (error) {
    console.error('Error getting climbing route:', error);
    throw error;
  }
};

// Create a new climbing route
export const createClimbingRoute = async (gymId: string, route: ClimbingRoute): Promise<ClimbingRoute> => {
  try {
    console.log(`Creating climbing route for gym ${gymId}`);
    
    // Get the gym first
    const gymRef = doc(db, 'gyms', gymId);
    const gymSnap = await getDoc(gymRef);
    
    if (!gymSnap.exists()) {
      console.error(`Gym with ID ${gymId} does not exist!`);
      throw new Error(`Cannot add route to non-existent gym: ${gymId}`);
    }
    
    // Create the gym object with existing data
    const gymDocData = gymSnap.data();
    const gym = Gym.fromFirestore({ ...gymDocData, id: gymId });
    
    // Make sure route has an ID
    if (!route.id) {
      route.id = `route_${Date.now()}`;
    }
    
    // Add the route to the gym
    console.log('Route data before adding to gym:', route.toFirestore());
    gym.addClimbingRoute(route);
    
    // Double-check there are no undefined values in the gym data
    const gymDataToSave = gym.toFirestore();
    console.log('Final gym data to save:', gymDataToSave);
    
    // Update the gym document with the new route
    await setDoc(gymRef, gymDataToSave);
    
    console.log(`Successfully saved route ${route.id} to gym ${gymId}`);
    return route;
  } catch (error) {
    console.error('Error creating climbing route:', error);
    throw error;
  }
};

// Update a climbing route
export const updateClimbingRoute = async (gymId: string, route: ClimbingRoute): Promise<void> => {
  try {
    console.log(`Updating climbing route ${route.id} for gym ${gymId}`);
    
    // Get the gym document
    const gymRef = doc(db, 'gyms', gymId);
    const gymSnap = await getDoc(gymRef);
    
    if (!gymSnap.exists()) {
      console.error(`Gym with ID ${gymId} does not exist!`);
      throw new Error(`Cannot update route in non-existent gym: ${gymId}`);
    }
    
    // Create the gym object from the data
    const gymDocData = gymSnap.data();
    const gym = Gym.fromFirestore({ ...gymDocData, id: gymId });
    
    // Add (or update) the route
    gym.addClimbingRoute(route);
    
    // Update the gym document
    await setDoc(gymRef, gym.toFirestore());
    console.log(`Successfully updated route ${route.id}`);
  } catch (error) {
    console.error('Error updating climbing route:', error);
    throw error;
  }
};

// Delete a climbing route
export const deleteClimbingRoute = async (gymId: string, routeId: string): Promise<void> => {
  try {
    console.log(`Deleting climbing route ${routeId} from gym ${gymId}`);
    
    // Get the gym document
    const gymRef = doc(db, 'gyms', gymId);
    const gymSnap = await getDoc(gymRef);
    
    if (!gymSnap.exists()) {
      console.error(`Gym with ID ${gymId} does not exist!`);
      throw new Error(`Cannot delete route from non-existent gym: ${gymId}`);
    }
    
    // Create the gym object from the data
    const gymDocData = gymSnap.data();
    const gym = Gym.fromFirestore({ ...gymDocData, id: gymId });
    
    // Remove the route by deleting its entry in the climbing_routes map
    if (gym.climbing_routes && gym.climbing_routes[routeId]) {
      delete gym.climbing_routes[routeId];
      
      // Update the gym document
      await setDoc(gymRef, gym.toFirestore());
      console.log(`Successfully deleted route ${routeId} from gym data`);
      
      // Now remove this route from the gym rankings document
      try {
        console.log(`Removing route ${routeId} from gym rankings`);
        
        // Get the ranking document
        const rankingRef = doc(db, 'gyms', gymId, 'rankings', 'current');
        const rankingDoc = await getDoc(rankingRef);
        
        if (rankingDoc.exists()) {
          // Get current rankings
          const rankings = rankingDoc.data().rankings || [];
          
          // Filter out entries for this route
          const updatedRankings = rankings.filter((entry: { routeId: string }) => 
            entry.routeId !== routeId
          );
          
          // Only update if we actually removed something
          if (updatedRankings.length !== rankings.length) {
            console.log(`Removed ${rankings.length - updatedRankings.length} entries from gym ranking for route ${routeId}`);
            
            // Update the ranking document
            await setDoc(rankingRef, { rankings: updatedRankings }, { merge: true });
          } else {
            console.log(`No matching entries found in rankings for route ${routeId}`);
          }
        } else {
          console.log(`No ranking document found for gym ${gymId}`);
        }
      } catch (rankingError) {
        console.error('Error updating gym ranking during route deletion:', rankingError);
        // Don't throw error here to avoid failing the main deletion operation
      }
      
    } else {
      console.warn(`Route ${routeId} not found in gym ${gymId}`);
    }
  } catch (error) {
    console.error('Error deleting climbing route:', error);
    throw error;
  }
};

// Archive a climbing route
export const archiveClimbingRoute = async (gymId: string, routeId: string): Promise<void> => {
  try {
    console.log(`Archiving climbing route ${routeId} from gym ${gymId}`);
    
    // First get the route details
    const route = await getClimbingRoute(gymId, routeId);
    if (!route) {
      console.error(`Route ${routeId} not found in gym ${gymId}`);
      throw new Error(`Cannot archive non-existent route: ${routeId}`);
    }
    
    // Get the complete climbing route data
    const routeData = route.toFirestore();
    
    // Create the archived document data with all route parameters
    const archivedData = {
      // Include all original route data
      id: routeData.id,
      style: routeData.style,
      grade: routeData.grade,
      color: routeData.color,
      rope: routeData.rope,
      setterId: routeData.setterId,
      setterName: routeData.setterName,
      note: routeData.note,
      comments: routeData.comments,
      date: routeData.date,
      status: "archived", // Override the original status to 'archived'
      picture: routeData.picture,
      location: routeData.location,
      points: routeData.points,
      
      // Add archiving metadata
      archivedAt: Date.now(),
      originalGymId: gymId
    };
    
    // Ensure we're not including any undefined values
    Object.keys(archivedData).forEach(key => {
      if (archivedData[key as keyof typeof archivedData] === undefined) {
        (archivedData as any)[key] = null;
      }
    });
    
    console.log('Archive data:', archivedData);
    
    // Create a reference to the archived_routes subcollection under the gym document
    const archivedRoutesRef = collection(db, 'gyms', gymId, 'archived_routes');
    await addDoc(archivedRoutesRef, archivedData);
    
    // Now delete the route from the gym (which also cleans up ranking entries)
    await deleteClimbingRoute(gymId, routeId);
    
    console.log(`Successfully archived route ${routeId}`);
  } catch (error) {
    console.error('Error archiving climbing route:', error);
    throw error;
  }
};
